import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { PageHero } from "@/components/page-hero";
import { getTool } from "@/content/tools";
import { RedundancyCalculator } from "@/components/calculators/redundancy";
import { HolidayCalculator } from "@/components/calculators/holiday";
import { SSPCalculator } from "@/components/calculators/ssp";
import { MaternityPayCalculator } from "@/components/calculators/maternity-pay";
import { PaternityPayCalculator } from "@/components/calculators/paternity-pay";
import { NoticePeriodCalculator } from "@/components/calculators/notice-period";
import { NationalMinimumWageCalculator } from "@/components/calculators/national-minimum-wage";
import { NMWDeepContent } from "@/components/calculators/nmw-deep-content";
import { UnfairDismissalCalculator } from "@/components/calculators/unfair-dismissal";
import { CTASection } from "@/components/cta-section";
import { SeoFAQ } from "@/components/seo-faq";
import { AccreditationsStrip } from "@/components/accreditations-strip";
import { EabBannerBlock } from "@/components/eab-banner";
import { Breadcrumbs } from "@/components/breadcrumbs";
import { ReviewStamp } from "@/components/review-stamp";
import { DEFAULT_AUTHOR } from "@/content/authors";
import { buildHead, softwareSchema, faqSchema, ensureFAQs } from "@/lib/seo";
import { RelatedContent } from "@/components/related-content";
import { getRelatedContent } from "@/lib/related";
import { DeepContentSection } from "@/components/deep-content";
import { getCalculatorDepth } from "@/content/calculator-depth";
import { CALCULATOR_FAQS } from "@/content/resource-depth";

const pkFor = (tool: { slug: string; primaryKeyword?: string }) =>
  tool.primaryKeyword ?? tool.slug.replace(/-/g, " ");

export const Route = createFileRoute("/hr-calculators/$slug")({
  loader: ({ params }) => {
    const tool = getTool(params.slug);
    if (!tool) throw notFound();
    return { tool };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const { tool } = loaderData;
    const pk = pkFor(tool);
    const path = `/hr-calculators/${tool.slug}`;
    // Breadcrumb JSON-LD is emitted once, by the visible trail on the page.
    const faqs = ensureFAQs(pk, CALCULATOR_FAQS[tool.slug] ?? [], "calculator");
    return buildHead({
      title: tool.metaTitle,
      description: tool.metaDescription,
      path,
      schemas: [
        softwareSchema({ name: tool.name, description: tool.metaDescription }),
        faqSchema(faqs),
      ],
    });
  },
  component: ToolPage,
});

function ToolPage() {
  const { tool } = Route.useLoaderData();
  const pk = pkFor(tool);
  return (
    <>
      <PageHero
        breadcrumbs={[
          { label: "Home", path: "/" },
          { label: "Calculators", path: "/hr-calculators" },
          { label: tool.name, path: `/hr-calculators/${tool.slug}` },
        ]}
        eyebrow="Calculators"
        title={tool.name}
        intro={tool.shortDescription}
        lastReviewed={tool.lastReviewed}
        author={tool.author ?? DEFAULT_AUTHOR}
      />



      <section className="px-6 py-12 lg:px-8 lg:py-16">
        <div className="mx-auto max-w-3xl">
          {tool.slug === "redundancy-pay-calculator" && <RedundancyCalculator />}
          {tool.slug === "holiday-entitlement-calculator" && <HolidayCalculator />}
          {tool.slug === "ssp-calculator" && <SSPCalculator />}
          {tool.slug === "maternity-pay-calculator" && <MaternityPayCalculator />}
          {tool.slug === "paternity-pay-calculator" && <PaternityPayCalculator />}
          {tool.slug === "notice-period-calculator" && <NoticePeriodCalculator />}
          {tool.slug === "national-minimum-wage-calculator" && <NationalMinimumWageCalculator />}
          {tool.slug === "unfair-dismissal-calculator" && <UnfairDismissalCalculator />}
        </div>
      </section>

      {tool.slug === "national-minimum-wage-calculator" && <NMWDeepContent />}
      {getCalculatorDepth(tool.slug) && (
        <DeepContentSection content={getCalculatorDepth(tool.slug)!} />
      )}


      {tool.hubService && (
        <section className="px-6 pb-4 lg:px-8">
          <div className="mx-auto max-w-3xl rounded-2xl border border-accent/30 bg-accent/5 p-6">
            <p className="text-xs font-semibold uppercase tracking-widest text-accent">Part of</p>
            <p className="mt-3 text-base leading-relaxed text-foreground">
              This calculator returns figures only. For end-to-end employer support, see{" "}
              <Link
                to="/services/$slug"
                params={{ slug: tool.hubService.slug }}
                className="font-medium text-brand underline decoration-accent underline-offset-4 hover:text-accent"
              >
                {tool.hubService.name}
              </Link>
              .
            </p>
          </div>
        </section>
      )}

      <EabBannerBlock path={`/hr-calculators/${tool.slug}`} source="calculator_page" />

      <AccreditationsStrip />
      <SeoFAQ
        primaryKeyword={pk}
        faqs={CALCULATOR_FAQS[tool.slug] ?? []}
        type="calculator"
        heading={`Questions employers ask about ${tool.name.toLowerCase().replace(/ calculator$| compliance checker$| estimator$/, "")}`}
      />

      <RelatedContent
        heading="Related guides and tools"
        items={getRelatedContent({
          type: "calculator",
          slug: tool.slug,
          primaryKeyword: pk,
          topics: [tool.category],
          diversify: true,
        })}
      />

      <CTASection
        headline="Need help with the next step?"
        sub="Our senior advisors can walk you through your specific situation, usually free for SMEs."
        primaryLabel="Talk to an HR expert"
      />
    </>
  );
}
